/**
 * MT: TR ön bilgilendirme onayı (snippets/mt-cart-extras.liquid, part 'cta'). custom-v1 sürümünün sadeleştirilmişi.
 *
 * Asıl güvence Liquid'de: kutu `required` ve sepet formuna bağlı (form="cart-form"); Ödeme butonunun native
 * gönderimi onay niteliğini kendisi yazar, JS'siz de işaretsiz gönderim olmaz. Bu dosya yalnız:
 * - kutu değişince niteliği /cart/update.js ile yazar/siler (çekmece ↔ sayfa, yeniden çizimde durum);
 * - işaretsizken Ödeme tıklanırsa gönderimi durdurur, hatayı gösterir, odağı kutuya taşır (buton aria-disabled, odaklanabilir);
 * - bekleyen nitelik isteği bitmeden ödemeye geçmez;
 * - sepet değişince onayı sıfırlar;
 * - belge linklerini sepeti terk ettirmeden <dialog> içinde açar (olmazsa yeni sekme).
 */
import { StandardEvents } from '@shopify/events';

/** @type {Set<Promise<unknown>>} */
const pending = new Set();
let savedOnServer = false;
/** @type {DOMStringMap | null} */
let lastData = null;
const CHECKOUT = 'button[name="checkout"]';

/** @returns {MtCartConsent | null} */
const current = () => /** @type {MtCartConsent | null} */ (document.querySelector('mt-cart-consent'));

/** @param {DOMStringMap | null} data @param {boolean} on */
function saveAttribute(data, on) {
  if (!data || !data.attrName || !data.updateUrl) return Promise.resolve();
  if (!on && !savedOnServer) return Promise.resolve();
  const request = fetch(data.updateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: { [data.attrName]: on ? data.attrValue : '', _mt_consent_cart: on ? data.fingerprint : '' },
    }),
  })
    .then((response) => {
      if (response.ok) savedOnServer = on;
    })
    .catch(() => {})
    .finally(() => pending.delete(request));
  pending.add(request);
  return request;
}

function syncCheckoutButtons() {
  const consent = current();
  const blocked = Boolean(consent && !consent.checkbox?.checked);
  for (const button of document.querySelectorAll(CHECKOUT)) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) continue;
    if (blocked) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }
}

class MtCartConsent extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  get checkbox() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="checkbox"]'));
  }

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onLinkClick);
    // `invalid` kabarcıklanmaz: yakalama evresinde dinlenir.
    this.addEventListener('invalid', this.#onInvalid, true);
    lastData = this.dataset;
    if (this.checkbox?.checked) savedOnServer = true;
    // Parmak izi tutmayan eski onay niteliği: doğrudan /checkout yolunda siparişe geçmesin.
    if (this.dataset.stale === 'true') {
      savedOnServer = true;
      saveAttribute(this.dataset, false);
    }
    // Horizon morph ile yeniden çizer: kutu durumu sunucudan gelir, butonu yeniden eşitle.
    const root = this.closest('.cart-drawer__summary, .cart-summary, cart-items-component') ?? this.parentElement;
    if (root) {
      this.#observer = new MutationObserver(syncCheckoutButtons);
      this.#observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['checked'] });
    }
    syncCheckoutButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onLinkClick);
    this.removeEventListener('invalid', this.#onInvalid, true);
    this.#observer?.disconnect();
    this.#observer = null;
    syncCheckoutButtons();
  }

  showError() {
    const box = this.checkbox;
    const error = this.querySelector('[data-mt-consent-error]');
    if (error) error.textContent = this.dataset.error || '';
    if (!box) return;
    box.setAttribute('aria-invalid', 'true');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    box.focus({ preventScroll: true });
  }

  clearError() {
    this.checkbox?.removeAttribute('aria-invalid');
    const error = this.querySelector('[data-mt-consent-error]');
    if (error) error.textContent = '';
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const box = this.checkbox;
    if (!box || event.target !== box) return;
    if (box.checked) this.clearError();
    saveAttribute(this.dataset, box.checked);
    syncCheckoutButtons();
  };

  /** @param {Event} event */
  #onInvalid = (event) => {
    event.preventDefault();
    this.showError();
  };

  /** @param {MouseEvent} event */
  #onLinkClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-mt-legal-modal]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    openLegalModal(link, this.dataset);
  };
}

/** @param {HTMLAnchorElement} link @param {DOMStringMap} labels */
async function openLegalModal(link, labels) {
  const dialog = document.createElement('dialog');
  dialog.className = 'mt-legal-modal';
  const titleId = `MtLegalModalTitle-${Date.now()}`;
  dialog.setAttribute('aria-labelledby', titleId);
  const header = document.createElement('div');
  header.className = 'mt-legal-modal__header';
  const title = document.createElement('h2');
  title.className = 'mt-legal-modal__title';
  title.id = titleId;
  title.textContent = (link.firstChild?.textContent || link.textContent || '').trim();
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'mt-legal-modal__close';
  close.setAttribute('aria-label', labels.modalClose || 'Close');
  close.textContent = '×';
  header.append(title, close);
  const body = document.createElement('div');
  body.className = 'mt-legal-modal__body';
  body.setAttribute('aria-live', 'polite');
  body.textContent = labels.modalLoading || '';
  dialog.append(header, body);
  document.body.append(dialog);
  close.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', () => {
    dialog.remove();
    link.focus();
  });
  dialog.showModal();
  close.focus();
  try {
    const response = await fetch(link.href, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(String(response.status));
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const main = doc.querySelector('#MainContent') || doc.querySelector('main');
    if (!main) throw new Error('no main');
    main.querySelectorAll('script, style, link, form, dialog, iframe').forEach((node) => node.remove());
    body.replaceChildren(...Array.from(main.childNodes).map((node) => document.importNode(node, true)));
  } catch {
    body.textContent = labels.modalError || '';
    const fallback = document.createElement('a');
    fallback.href = link.href;
    fallback.target = '_blank';
    fallback.rel = 'noopener';
    fallback.textContent = title.textContent || link.href;
    body.append(' ', fallback);
  }
}

if (!customElements.get('mt-cart-consent')) {
  customElements.define('mt-cart-consent', MtCartConsent);

  // Ödeme: işaretsizse durdur; bekleyen nitelik isteği varsa bitince gönder.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(CHECKOUT) : null;
      if (!(button instanceof HTMLButtonElement)) return;
      const consent = current();
      if (!consent) return;
      if (!consent.checkbox?.checked) {
        event.preventDefault();
        event.stopImmediatePropagation();
        consent.showError();
        return;
      }
      if (pending.size > 0) {
        event.preventDefault();
        const form = button.form;
        Promise.allSettled(Array.from(pending)).then(() => form?.requestSubmit(button));
      }
    },
    true
  );

  // Sepet değişince onay sıfırlanır (onaylanan özet = o anki sepet).
  /** @param {Event & { promise?: Promise<unknown> }} event */
  const onCartChange = (event) => {
    (event.promise ?? Promise.resolve())
      .catch(() => {})
      .then(() => {
        const consent = current();
        if (consent?.checkbox) {
          consent.checkbox.checked = false;
          consent.clearError();
        }
        saveAttribute(consent?.dataset ?? lastData, false);
        syncCheckoutButtons();
      });
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartChange);
  document.addEventListener(StandardEvents.cartDiscountUpdate, onCartChange);
}
